import React, { useEffect, useState, Fragment } from 'react';
import { ExternalLink, AlertCircle, Columns3, List, ChevronDown, ChevronRight, Sparkles } from 'lucide-react';
import { FilterBar, FilterDef, SortOption } from './FilterBar';
import { motion, AnimatePresence } from 'framer-motion';
import { GpBadge } from './GpBadge';
import { SourceAnnotation } from './SourceAnnotation';
type Stage = 'Not Started' | 'DDQ / Diligence' | 'TA Drafting' | 'Pending Execution' | 'Complete';
type Urgency = 'high' | 'medium' | 'low';
type InvoiceStatus = 'Invoice Not Issued' | 'Invoice Pending Payment' | 'Invoice Circulating / Sent' | 'Payment Confirmed / Paid';
export interface ConsentFilter {
  key: string;
  value: string;
}
export const TODAY = new Date(2026, 2, 1); // March 1, 2026
function parseDate(dateStr: string): Date {
  if (!dateStr) return new Date();
  const cleaned = dateStr.replace(/'/g, '');
  const parts = cleaned.split(' ');
  if (parts.length !== 3) return new Date();
  const day = parseInt(parts[0]);
  const monthMap: Record<string, number> = {
    Jan: 0,
    Feb: 1,
    Mar: 2,
    Apr: 3,
    May: 4,
    Jun: 5,
    Jul: 6,
    Aug: 7,
    Sep: 8,
    Oct: 9,
    Nov: 10,
    Dec: 11
  };
  const month = monthMap[parts[1]];
  const year = 2000 + parseInt(parts[2]);
  return new Date(year, month, day);
}
export function getElapsedDays(dateStr: string): number {
  if (!dateStr) return 0;
  const date = parseDate(dateStr);
  const diff = TODAY.getTime() - date.getTime();
  return Math.floor(diff / (1000 * 60 * 60 * 24));
}
function formatElapsedCompact(days: number): string {
  if (days < 1) return '0d';
  if (days < 30) return `${days}d`;
  const months = Math.floor(days / 30);
  return `${months}mo`;
}
type ElapsedTier = 'good' | 'caution' | 'stale';
function getElapsedTier(days: number): ElapsedTier {
  if (days <= 7) return 'good';
  if (days <= 14) return 'caution';
  return 'stale';
}
const TIER_TEXT_COLOR: Record<ElapsedTier, string> = {
  good: 'text-gray-500',
  caution: 'text-amber-600',
  stale: 'text-rose-600'
};
function getInvoiceColor(status: string): string {
  switch (status) {
    case 'Invoice Not Issued':
      return 'bg-gray-100 text-gray-500';
    case 'Invoice Pending Payment':
    case 'Invoice Circulating / Sent':
      return 'bg-amber-50 text-amber-600';
    case 'Payment Confirmed / Paid':
      return 'bg-emerald-50 text-emerald-600';
    default:
      return 'bg-gray-100 text-gray-500';
  }
}
const STAGES: Stage[] = ['Not Started', 'DDQ / Diligence', 'TA Drafting', 'Pending Execution', 'Complete'];
interface UnifiedConsent {
  id: string;
  partnership: string;
  gp: string;
  taStatus: string;
  nextAction: string;
  lastComm: string;
  lastCommDate: string;
  invoice: string;
  invoiceAmount?: string;
  invoiceSplit?: string;
  urgency?: Urgency;
  statusCategory?: string;
  stage: Stage;
  aiBanner?: boolean;
  rawElapsedDays: number;
}
// --- RAW DATA ---
export const CONSENTS_DATA = [{
  id: 1,
  fundManager: 'Collingwood Energy',
  partnerships: ['Collingwood Energy Opportunities Fund', 'Collingwood Energy Infrastructure Fund II'],
  partnershipDetails: [{
    partnership: 'Collingwood Energy Opportunities Fund',
    nextAction: 'Obtain GP confirmation of transfer approval.',
    taStatus: 'GP approval expected imminently.',
    invoiceAmount: '$15,000.00',
    invoiceSplit: '60% / 40%'
  }, {
    partnership: 'Collingwood Energy Infrastructure Fund II',
    nextAction: 'Request final execution version of the transfer agreement for circulation.',
    taStatus: 'No further TA comments anticipated; execution pending GP confirmation.',
    invoiceAmount: '$12,500.00',
    invoiceSplit: '60% / 40%'
  }],
  nextAction: 'Obtain GP confirmation of transfer approval and request final execution version of the transfer agreement for circulation.',
  urgency: 'high' as Urgency,
  lastCorrespondence: "24 Feb '26",
  taStatus: 'GP approval expected imminently; no further TA comments anticipated; execution pending GP confirmation.',
  statusCategory: 'Pending Execution',
  invoiceStatus: 'Invoice Circulating / Sent'
}, {
  id: 2,
  fundManager: 'Gatineau Real Assets',
  partnerships: ['Gatineau Real Assets Fund'],
  partnershipDetails: [{
    partnership: 'Gatineau Real Assets Fund',
    invoiceAmount: '$7,500.00',
    invoiceSplit: '50% / 50%'
  }],
  nextAction: 'Deliver remaining KYC attachments to fund administrator and circulate final execution version of the transfer agreement.',
  urgency: 'high' as Urgency,
  lastCorrespondence: "20 Feb '26",
  taStatus: 'TA agreed in principle; only KYC attachments outstanding prior to execution circulation.',
  statusCategory: 'Pending Execution',
  invoiceStatus: 'Invoice Pending Payment'
}, {
  id: 3,
  fundManager: 'Kingston Partners',
  partnerships: ['Kingston Fund III', 'Kingston Growth Equity Fund IV', 'Kingston Asia-Pacific Fund I'],
  partnershipDetails: [{
    partnership: 'Kingston Fund III',
    invoiceAmount: '$10,000.00',
    invoiceSplit: '70% / 30%'
  }, {
    partnership: 'Kingston Growth Equity Fund IV',
    invoiceAmount: '$8,750.00',
    invoiceSplit: '70% / 30%'
  }, {
    partnership: 'Kingston Asia-Pacific Fund I',
    nextAction: 'Follow up on specific Asia-Pacific regulatory requirements.',
    taStatus: 'Awaiting local counsel confirmation.',
    invoiceAmount: '$4,000.00',
    invoiceSplit: '70% / 30%'
  }],
  nextAction: 'Await GP counsel circulation of draft Transfer Agreement and buyer confirmation regarding staple.',
  urgency: 'low' as Urgency,
  lastCorrespondence: "10 Jun '25",
  taStatus: 'TA and KYC documentation not yet circulated.',
  statusCategory: 'Not Yet Circulated',
  invoiceStatus: 'Invoice Pending Payment'
}, {
  id: 4,
  fundManager: 'Laval Global',
  partnerships: ['Laval Global Partners Fund II', 'Laval Global Credit Opportunities Fund'],
  partnershipDetails: [{
    partnership: 'Laval Global Partners Fund II',
    nextAction: 'Fully executed; closing notice issued.',
    taStatus: 'Complete — no outstanding items.',
    invoiceAmount: '$3,000.00',
    invoiceSplit: '50% / 50%'
  }, {
    partnership: 'Laval Global Credit Opportunities Fund',
    nextAction: 'Post-closing signature page circulation pending.',
    taStatus: 'Substantively complete; administrative signature page outstanding.',
    invoiceAmount: '$2,000.00',
    invoiceSplit: '50% / 50%'
  }],
  nextAction: 'Confirm closing notice and release executed documents from escrow.',
  urgency: 'low' as Urgency,
  lastCorrespondence: "9 Jun '25",
  taStatus: 'Fully executed; closing notice issued; no outstanding TA or consent items.',
  statusCategory: 'Fully Executed',
  invoiceStatus: 'Payment Confirmed / Paid'
}, {
  id: 5,
  fundManager: 'Maple Tree',
  partnerships: ['MapleTree Private Equity Fund I'],
  partnershipDetails: [{
    partnership: 'MapleTree Private Equity Fund I',
    invoiceAmount: '$9,250.00',
    invoiceSplit: '70% / 30%'
  }],
  nextAction: 'Follow up on side-letter assignment and confirm if TA revisions required.',
  urgency: 'medium' as Urgency,
  lastCorrespondence: "22 Feb '26",
  taStatus: 'TA substantially agreed; execution pending buyer response.',
  statusCategory: 'Pending Execution',
  invoiceStatus: 'Invoice Circulating / Sent'
}, {
  id: 6,
  fundManager: 'Niagara Growth',
  partnerships: ['Niagara Growth Equity Fund V', 'Niagara Growth Venture Fund III'],
  partnershipDetails: [{
    partnership: 'Niagara Growth Equity Fund V',
    nextAction: 'KYC/AML review initiated; awaiting buyer entity confirmation.',
    taStatus: 'KYC pack submitted; under review by fund administrator.',
    invoiceAmount: '$16,000.00',
    invoiceSplit: '60% / 40%'
  }, {
    partnership: 'Niagara Growth Venture Fund III',
    nextAction: 'Buyer yet to confirm investing entity; KYC not started.',
    taStatus: 'No KYC documentation submitted; pending entity confirmation.',
    invoiceAmount: '$12,000.00',
    invoiceSplit: '60% / 40%'
  }],
  nextAction: 'Begin KYC/AML reviews for the transferee entities and track the potential transferees.',
  urgency: 'high' as Urgency,
  lastCorrespondence: "18 Feb '26",
  taStatus: 'TA/GP consent still not launched; Buyer focused on confirming investing entities and KYC; no execution or invoice payment steps initiated.',
  statusCategory: 'Not Yet Circulated',
  invoiceStatus: 'Invoice Not Issued'
}, {
  id: 7,
  fundManager: 'Sherbrooke Capital Partners',
  partnerships: ['Sherbrooke Capital Secondary Fund IV'],
  partnershipDetails: [{
    partnership: 'Sherbrooke Capital Secondary Fund IV',
    invoiceAmount: '$6,000.00',
    invoiceSplit: '50% / 50%'
  }],
  nextAction: 'Confirm transferee legal names and SMA structures; request TA and KYC pack circulation.',
  urgency: 'medium' as Urgency,
  lastCorrespondence: "26 Feb '26",
  taStatus: 'Only DDQ responses received; TA and KYC documentation not yet circulated.',
  statusCategory: 'Not Yet Circulated',
  invoiceStatus: 'Invoice Not Issued'
}, {
  id: 8,
  fundManager: 'Stratford Credit',
  partnerships: ['Stratford Credit Fund V', 'Stratford Credit Opportunities Fund II', 'Stratford Senior Lending Fund'],
  partnershipDetails: [{
    partnership: 'Stratford Credit Fund V',
    nextAction: 'Invoice paid; awaiting fund administrator release confirmation.',
    taStatus: 'Fully executed; release pending administrator confirmation.',
    invoiceAmount: '$22,750.00',
    invoiceSplit: '70% / 30%'
  }, {
    partnership: 'Stratford Credit Opportunities Fund II',
    nextAction: 'Countersignature received; invoice issued and pending payment.',
    taStatus: 'TA countersigned; invoice payment pending.',
    invoiceAmount: '$5,000.00',
    invoiceSplit: '50% / 50%'
  }, {
    partnership: 'Stratford Senior Lending Fund',
    nextAction: 'TA execution pending; awaiting buyer countersignature.',
    taStatus: 'Seller-signed; buyer countersignature outstanding.',
    invoiceAmount: '$28,000.00',
    invoiceSplit: '60% / 40%'
  }],
  nextAction: 'Pay outstanding invoice and confirm fund administrator release and closing notice following countersignature.',
  urgency: 'high' as Urgency,
  lastCorrespondence: "14 Feb '26",
  taStatus: 'TA countersigned by IronStone holding; invoice issued and pending payment prior to closing release.',
  statusCategory: 'Pending Execution',
  invoiceStatus: 'Invoice Pending Payment'
}, {
  id: 9,
  fundManager: 'Waterloo Capital',
  partnerships: ['Waterloo Diversified Fund LP II', 'Waterloo Capital Real Estate Fund'],
  partnershipDetails: [{
    partnership: 'Waterloo Diversified Fund LP II',
    nextAction: 'Finalize transferee jurisdictions/number.',
    taStatus: 'Seller revisions pending.',
    invoiceAmount: '$22,000.00',
    invoiceSplit: '50% / 50%'
  }, {
    partnership: 'Waterloo Capital Real Estate Fund',
    nextAction: 'Initiate invoice payment.',
    taStatus: 'GP form TA redlined; GP consent and execution pending.',
    invoiceAmount: '$16,250.00',
    invoiceSplit: '50% / 50%'
  }],
  nextAction: 'Buyer and Seller counsel to incorporate comments into TA redline, finalize transferee jurisdictions/number; initiate invoice payment.',
  urgency: 'high' as Urgency,
  lastCorrespondence: "25 Feb '26",
  taStatus: 'GP form TA redlined; seller revisions pending; GP consent and execution pending.',
  statusCategory: 'Not Yet Circulated',
  invoiceStatus: 'Invoice Circulating / Sent'
}];
const DIAMOND_CONSENTS = [{
  id: 1,
  partnership: 'Antin Infrastructure Fund III (Pri)',
  gp: 'Antin Infrastructure Partners',
  taStatus: '',
  nextAction: '',
  lastComm: '6d',
  lastCommDate: "7 Apr '26",
  invoice: 'Invoice Not Issued',
  invoiceAmount: '$18,000.00',
  invoiceSplit: '60% / 40%',
  stage: 'Not Started' as Stage
}, {
  id: 2,
  partnership: 'Ardian Americas Infrastructure Fund IV S.C.S., SICAV-RAIF',
  gp: 'Ardian',
  taStatus: '',
  nextAction: '',
  lastComm: '6d',
  lastCommDate: "7 Apr '26",
  invoice: 'Invoice Not Issued',
  invoiceAmount: '$25,000.00',
  invoiceSplit: '70% / 30%',
  stage: 'Not Started' as Stage
}, {
  id: 3,
  partnership: 'Asterion Industrial Infra Fund I',
  gp: 'Asterion Industrial Partners',
  taStatus: '',
  nextAction: '',
  lastComm: '6d',
  lastCommDate: "7 Apr '26",
  invoice: 'Invoice Not Issued',
  invoiceAmount: '$14,500.00',
  invoiceSplit: '50% / 50%',
  stage: 'Not Started' as Stage
}, {
  id: 4,
  partnership: 'Brookfield Infrastructure Fund II, L.P.',
  gp: 'Brookfield',
  taStatus: '',
  nextAction: 'Akin to review partial DDQ responses; await Brookfield/Weil tax section responses and follow up by Monday deadline.',
  lastComm: '5d',
  lastCommDate: "7 Apr '26",
  invoice: 'Invoice Circulating / Sent',
  invoiceAmount: '$35,000.00',
  invoiceSplit: '60% / 40%',
  stage: 'DDQ / Diligence' as Stage
}, {
  id: 5,
  partnership: 'DIF Core Infrastructure Fund I, L.P.',
  gp: 'DIF Capital Partners',
  taStatus: '',
  nextAction: '',
  lastComm: '6d',
  lastCommDate: "7 Apr '26",
  invoice: 'Invoice Not Issued',
  invoiceAmount: '$11,000.00',
  invoiceSplit: '50% / 50%',
  stage: 'Not Started' as Stage
}, {
  id: 6,
  partnership: 'DigitalBridgePartners',
  gp: 'DigitalBridge',
  taStatus: '',
  nextAction: '',
  lastComm: '6d',
  lastCommDate: "7 Apr '26",
  invoice: 'Invoice Not Issued',
  invoiceAmount: '$20,000.00',
  invoiceSplit: '70% / 30%',
  stage: 'Not Started' as Stage
}, {
  id: 7,
  partnership: 'EQT Infrastructure III Co-Investment (A) SCSp',
  gp: 'EQT',
  taStatus: '',
  nextAction: 'Await EQT DDQ responses and notify EQT of selected buyer.',
  lastComm: '4d',
  lastCommDate: "9 Apr '26",
  invoice: 'Invoice Pending Payment',
  invoiceAmount: '$42,000.00',
  invoiceSplit: '60% / 40%',
  stage: 'DDQ / Diligence' as Stage
}, {
  id: 8,
  partnership: 'EQT Infrastructure IV (No.2) EUR SCSp',
  gp: 'EQT',
  taStatus: '',
  nextAction: '',
  lastComm: '',
  lastCommDate: '',
  invoice: '',
  invoiceAmount: '$38,500.00',
  invoiceSplit: '60% / 40%',
  aiBanner: true,
  stage: 'Not Started' as Stage
}, {
  id: 9,
  partnership: 'EQT Infrastructure IV Co-Investment (F) SCSp',
  gp: 'EQT',
  taStatus: '',
  nextAction: '',
  lastComm: '',
  lastCommDate: '',
  invoice: '',
  invoiceAmount: '$27,000.00',
  invoiceSplit: '60% / 40%',
  aiBanner: true,
  stage: 'Not Started' as Stage
}, {
  id: 10,
  partnership: 'EQT Saber Side Car (No. 1) EUR SCSp',
  gp: 'EQT',
  taStatus: '',
  nextAction: '',
  lastComm: '',
  lastCommDate: '',
  invoice: '',
  invoiceAmount: '$15,750.00',
  invoiceSplit: '60% / 40%',
  aiBanner: true,
  stage: 'Not Started' as Stage
}, {
  id: 11,
  partnership: 'InfraVia European Fund IV',
  gp: 'InfraVia Capital Partners',
  taStatus: '',
  nextAction: '',
  lastComm: '6d',
  lastCommDate: "7 Apr '26",
  invoice: 'Invoice Not Issued',
  invoiceAmount: '$16,500.00',
  invoiceSplit: '50% / 50%',
  stage: 'Not Started' as Stage
}, {
  id: 12,
  partnership: 'InstarAGF Essential Infrastructure Fund',
  gp: 'InstarAGF Asset Management',
  taStatus: 'TA not yet circulated; no drafting/negotiation or signature process commenced.',
  nextAction: 'Confirm DDQ review complete, finalize ROFO process timing, obtain audited 2025 Fund I financials, and advance TA drafting.',
  lastComm: '6d',
  lastCommDate: "7 Apr '26",
  invoice: 'Invoice Circulating / Sent',
  invoiceAmount: '$30,000.00',
  invoiceSplit: '70% / 30%',
  stage: 'DDQ / Diligence' as Stage
}, {
  id: 13,
  partnership: 'KKR Global Infrastructure Investors III, L.P.',
  gp: 'KKR',
  taStatus: '',
  nextAction: "Confirm with prospective buyers that final purchaser is not a '40 Act Vehicle per KKR requirement.",
  lastComm: '6d',
  lastCommDate: "7 Apr '26",
  invoice: 'Invoice Pending Payment',
  invoiceAmount: '$45,000.00',
  invoiceSplit: '50% / 50%',
  stage: 'DDQ / Diligence' as Stage
}, {
  id: 14,
  partnership: 'Macquarie Asia Infrastructure Fund 2',
  gp: 'Macquarie',
  taStatus: '',
  nextAction: '',
  lastComm: '6d',
  lastCommDate: "7 Apr '26",
  invoice: 'Invoice Not Issued',
  invoiceAmount: '$22,000.00',
  invoiceSplit: '60% / 40%',
  stage: 'Not Started' as Stage
}, {
  id: 15,
  partnership: 'Northleaf Infrastructure Capital Partners III, L.P.',
  gp: 'Northleaf Capital Partners',
  taStatus: '',
  nextAction: '',
  lastComm: '6d',
  lastCommDate: "7 Apr '26",
  invoice: 'Invoice Not Issued',
  invoiceAmount: '$19,500.00',
  invoiceSplit: '50% / 50%',
  stage: 'Not Started' as Stage
}];
const CASCADE_CONSENTS = [{
  id: 1,
  partnership: 'Apollo Investment Fund IX, L.P.',
  gp: 'Apollo Global Management',
  taStatus: 'Fully executed and released.',
  nextAction: '',
  lastComm: '2d',
  lastCommDate: "10 Apr '26",
  invoice: 'Payment Confirmed / Paid',
  invoiceAmount: '$75,000.00',
  invoiceSplit: '70% / 30%',
  stage: 'Complete' as Stage
}, {
  id: 2,
  partnership: 'Apollo Investment Fund VIII, L.P.',
  gp: 'Apollo Global Management',
  taStatus: 'Fully executed and released.',
  nextAction: '',
  lastComm: '2d',
  lastCommDate: "10 Apr '26",
  invoice: 'Payment Confirmed / Paid',
  invoiceAmount: '$45,000.00',
  invoiceSplit: '70% / 30%',
  stage: 'Complete' as Stage
}, {
  id: 3,
  partnership: 'Ares Corporate Opportunities Fund V, L.P.',
  gp: 'Ares Management',
  taStatus: 'TA drafting in progress; awaiting buyer comments.',
  nextAction: 'Buyer counsel to provide comments on draft TA.',
  lastComm: '4d',
  lastCommDate: "8 Apr '26",
  invoice: 'Invoice Circulating / Sent',
  invoiceAmount: '$32,000.00',
  invoiceSplit: '60% / 40%',
  stage: 'TA Drafting' as Stage
}, {
  id: 4,
  partnership: 'Bain Capital Fund XII, L.P.',
  gp: 'Bain Capital',
  taStatus: 'Fully executed and released.',
  nextAction: '',
  lastComm: '10d',
  lastCommDate: "2 Apr '26",
  invoice: 'Payment Confirmed / Paid',
  invoiceAmount: '$55,000.00',
  invoiceSplit: '50% / 50%',
  stage: 'Complete' as Stage
}, {
  id: 5,
  partnership: 'Blackstone Capital Partners VII L.P.',
  gp: 'Blackstone',
  taStatus: 'DDQ responses pending.',
  nextAction: 'Awaiting GP responses to DDQ.',
  lastComm: '7d',
  lastCommDate: "5 Apr '26",
  invoice: 'Invoice Not Issued',
  invoiceAmount: '$60,000.00',
  invoiceSplit: '70% / 30%',
  stage: 'DDQ / Diligence' as Stage
}, {
  id: 6,
  partnership: 'Blackstone Real Estate Partners VIII L.P.',
  gp: 'Blackstone',
  taStatus: 'DDQ responses pending.',
  nextAction: 'Awaiting GP responses to DDQ.',
  lastComm: '7d',
  lastCommDate: "5 Apr '26",
  invoice: 'Invoice Not Issued',
  invoiceAmount: '$48,000.00',
  invoiceSplit: '70% / 30%',
  stage: 'DDQ / Diligence' as Stage
}, {
  id: 7,
  partnership: 'Brookfield Capital Partners IV, L.P.',
  gp: 'Brookfield Asset Management',
  taStatus: 'TA drafted; blocked on tax withholding confirmation.',
  nextAction: 'Resolve tax withholding issues with GP counsel.',
  lastComm: '1d',
  lastCommDate: "11 Apr '26",
  invoice: 'Invoice Pending Payment',
  invoiceAmount: '$40,000.00',
  invoiceSplit: '60% / 40%',
  stage: 'TA Drafting' as Stage
}, {
  id: 8,
  partnership: 'Carlyle Partners VI, L.P.',
  gp: 'Carlyle Group',
  taStatus: 'Execution pending buyer signature.',
  nextAction: 'Buyer to sign and return TA.',
  lastComm: '3d',
  lastCommDate: "9 Apr '26",
  invoice: 'Invoice Pending Payment',
  invoiceAmount: '$52,500.00',
  invoiceSplit: '80% / 20%',
  stage: 'Pending Execution' as Stage
}, {
  id: 9,
  partnership: 'Carlyle Europe Partners IV, L.P.',
  gp: 'Carlyle Group',
  taStatus: 'Execution pending buyer signature.',
  nextAction: 'Buyer to sign and return TA.',
  lastComm: '3d',
  lastCommDate: "9 Apr '26",
  invoice: 'Invoice Pending Payment',
  invoiceAmount: '$38,000.00',
  invoiceSplit: '60% / 40%',
  stage: 'Pending Execution' as Stage
}, {
  id: 10,
  partnership: 'Carlyle Asia Partners IV, L.P.',
  gp: 'Carlyle Group',
  taStatus: 'Execution pending buyer signature.',
  nextAction: 'Buyer to sign and return TA.',
  lastComm: '3d',
  lastCommDate: "9 Apr '26",
  invoice: 'Invoice Pending Payment',
  invoiceAmount: '$28,500.00',
  invoiceSplit: '50% / 50%',
  stage: 'Pending Execution' as Stage
}, {
  id: 11,
  partnership: 'CVC Capital Partners VI (A) L.P.',
  gp: 'CVC Capital Partners',
  taStatus: 'Not yet circulated.',
  nextAction: 'Prepare initial outreach to GP.',
  lastComm: '14d',
  lastCommDate: "29 Mar '26",
  invoice: 'Invoice Not Issued',
  invoiceAmount: '$35,000.00',
  invoiceSplit: '50% / 50%',
  stage: 'Not Started' as Stage
}, {
  id: 12,
  partnership: 'EQT VII (No.1) EUR SCSp',
  gp: 'EQT Partners',
  taStatus: 'TA drafting in progress.',
  nextAction: 'Seller counsel to circulate revised draft.',
  lastComm: '2d',
  lastCommDate: "10 Apr '26",
  invoice: 'Invoice Circulating / Sent',
  invoiceAmount: '$30,000.00',
  invoiceSplit: '60% / 40%',
  stage: 'TA Drafting' as Stage
}, {
  id: 13,
  partnership: 'EQT VII (No.2) EUR SCSp',
  gp: 'EQT Partners',
  taStatus: 'TA drafting in progress.',
  nextAction: 'Seller counsel to circulate revised draft.',
  lastComm: '2d',
  lastCommDate: "10 Apr '26",
  invoice: 'Invoice Not Issued',
  invoiceAmount: '$25,500.00',
  invoiceSplit: '60% / 40%',
  stage: 'TA Drafting' as Stage
}, {
  id: 14,
  partnership: 'EQT Mid Market Europe SCSp',
  gp: 'EQT Partners',
  taStatus: 'TA drafting in progress.',
  nextAction: 'Seller counsel to circulate revised draft.',
  lastComm: '2d',
  lastCommDate: "10 Apr '26",
  invoice: 'Invoice Not Issued',
  invoiceAmount: '$18,000.00',
  invoiceSplit: '60% / 40%',
  stage: 'TA Drafting' as Stage
}, {
  id: 15,
  partnership: 'EQT Credit Opportunities III SCSp',
  gp: 'EQT Partners',
  taStatus: 'TA drafting in progress.',
  nextAction: 'Seller counsel to circulate revised draft.',
  lastComm: '2d',
  lastCommDate: "10 Apr '26",
  invoice: 'Invoice Not Issued',
  invoiceAmount: '$22,000.00',
  invoiceSplit: '60% / 40%',
  stage: 'TA Drafting' as Stage
}, {
  id: 16,
  partnership: 'Francisco Partners IV, L.P.',
  gp: 'Francisco Partners',
  taStatus: 'Fully executed and released.',
  nextAction: '',
  lastComm: '12d',
  lastCommDate: "31 Mar '26",
  invoice: 'Payment Confirmed / Paid',
  invoiceAmount: '$42,000.00',
  invoiceSplit: '50% / 50%',
  stage: 'Complete' as Stage
}, {
  id: 17,
  partnership: 'General Atlantic Investment Partners 2015, L.P.',
  gp: 'General Atlantic',
  taStatus: 'Not yet circulated.',
  nextAction: 'Prepare initial outreach to GP.',
  lastComm: '15d',
  lastCommDate: "28 Mar '26",
  invoice: 'Invoice Not Issued',
  invoiceAmount: '$28,000.00',
  invoiceSplit: '70% / 30%',
  stage: 'Not Started' as Stage
}, {
  id: 18,
  partnership: 'Hellman & Friedman Capital Partners VIII, L.P.',
  gp: 'Hellman & Friedman',
  taStatus: 'TA drafting in progress.',
  nextAction: 'Awaiting GP comments on draft TA.',
  lastComm: '5d',
  lastCommDate: "7 Apr '26",
  invoice: 'Invoice Circulating / Sent',
  invoiceAmount: '$36,000.00',
  invoiceSplit: '50% / 50%',
  stage: 'TA Drafting' as Stage
}, {
  id: 19,
  partnership: 'HgCapital 7 L.P.',
  gp: 'Hg Capital',
  taStatus: 'Blocked on KYC/AML.',
  nextAction: 'Buyer to provide additional KYC documentation to fund admin.',
  lastComm: '1d',
  lastCommDate: "11 Apr '26",
  invoice: 'Invoice Pending Payment',
  invoiceAmount: '$20,000.00',
  invoiceSplit: '60% / 40%',
  stage: 'DDQ / Diligence' as Stage
}, {
  id: 20,
  partnership: 'Insight Venture Partners IX, L.P.',
  gp: 'Insight Partners',
  taStatus: 'DDQ responses pending.',
  nextAction: 'Awaiting GP responses to DDQ.',
  lastComm: '6d',
  lastCommDate: "6 Apr '26",
  invoice: 'Invoice Circulating / Sent',
  invoiceAmount: '$33,000.00',
  invoiceSplit: '70% / 30%',
  stage: 'DDQ / Diligence' as Stage
}, {
  id: 21,
  partnership: 'KKR Americas Fund XII, L.P.',
  gp: 'KKR & Co',
  taStatus: 'Fully executed and released.',
  nextAction: '',
  lastComm: '8d',
  lastCommDate: "4 Apr '26",
  invoice: 'Payment Confirmed / Paid',
  invoiceAmount: '$85,000.00',
  invoiceSplit: '60% / 40%',
  stage: 'Complete' as Stage
}, {
  id: 22,
  partnership: 'KKR Asian Fund III L.P.',
  gp: 'KKR & Co',
  taStatus: 'Fully executed and released.',
  nextAction: '',
  lastComm: '8d',
  lastCommDate: "4 Apr '26",
  invoice: 'Payment Confirmed / Paid',
  invoiceAmount: '$62,000.00',
  invoiceSplit: '60% / 40%',
  stage: 'Complete' as Stage
}, {
  id: 23,
  partnership: 'KKR European Fund IV L.P.',
  gp: 'KKR & Co',
  taStatus: 'Fully executed and released.',
  nextAction: '',
  lastComm: '8d',
  lastCommDate: "4 Apr '26",
  invoice: 'Payment Confirmed / Paid',
  invoiceAmount: '$58,000.00',
  invoiceSplit: '60% / 40%',
  stage: 'Complete' as Stage
}, {
  id: 24,
  partnership: 'Green Equity Investors VII, L.P.',
  gp: 'Leonard Green & Partners',
  taStatus: 'TA drafting in progress.',
  nextAction: 'Seller counsel to circulate initial draft.',
  lastComm: '3d',
  lastCommDate: "9 Apr '26",
  invoice: 'Invoice Not Issued',
  invoiceAmount: '$27,500.00',
  invoiceSplit: '50% / 50%',
  stage: 'TA Drafting' as Stage
}];
// --- NORMALIZATION ---
function getStageFromStatus(statusCategory: string, taStatus: string): Stage {
  if (statusCategory === 'Fully Executed') return 'Complete';
  if (statusCategory === 'Pending Execution') return 'Pending Execution';
  if (taStatus.toLowerCase().includes('ddq') || taStatus.toLowerCase().includes('kyc')) return 'DDQ / Diligence';
  return 'Not Started';
}
export function normalizeData(projectName: string): UnifiedConsent[] {
  if (projectName === 'Diamond') {
    return DIAMOND_CONSENTS.map(c => ({
      ...c,
      id: c.id.toString(),
      statusCategory: c.stage === 'Complete' ? 'Fully Executed' : c.stage === 'Pending Execution' ? 'Pending Execution' : 'Not Yet Circulated',
      rawElapsedDays: c.lastCommDate ? getElapsedDays(c.lastCommDate) : 0
    }));
  }
  if (projectName === 'Cascade') {
    return CASCADE_CONSENTS.map(c => ({
      ...c,
      id: c.id.toString(),
      statusCategory: c.stage === 'Complete' ? 'Fully Executed' : c.stage === 'Pending Execution' ? 'Pending Execution' : 'Not Yet Circulated',
      rawElapsedDays: c.lastCommDate ? getElapsedDays(c.lastCommDate) : 0
    }));
  }
  const flattened: UnifiedConsent[] = [];
  CONSENTS_DATA.forEach(group => {
    const elapsedDays = getElapsedDays(group.lastCorrespondence);
    const compactComm = formatElapsedCompact(elapsedDays);
    const stage = getStageFromStatus(group.statusCategory, group.taStatus);
    group.partnerships.forEach((partnership, idx) => {
      const detail = group.partnershipDetails?.find(d => d.partnership === partnership);
      flattened.push({
        id: `${group.id}-${idx}`,
        partnership,
        gp: group.fundManager,
        taStatus: detail?.taStatus || group.taStatus,
        nextAction: detail?.nextAction || group.nextAction,
        lastComm: compactComm,
        lastCommDate: group.lastCorrespondence,
        invoice: group.invoiceStatus,
        invoiceAmount: detail?.invoiceAmount,
        invoiceSplit: detail?.invoiceSplit,
        urgency: group.urgency,
        statusCategory: group.statusCategory,
        stage,
        rawElapsedDays: elapsedDays
      });
    });
  });
  return flattened;
}
export function ConsentsTab({
  projectName,
  initialFilter,
  onGpSelect
}: {
  projectName: string;
  initialFilter?: ConsentFilter | null;
  onGpSelect?: (gp: string, tab?: string) => void;
}) {
  const [view, setView] = useState<'pipeline' | 'gp'>('gp');
  const [statusFilter, setStatusFilter] = useState('');
  const [gpFilter, setGpFilter] = useState('');
  const [urgencyFilter, setUrgencyFilter] = useState('');
  const [staleOnly, setStaleOnly] = useState(false);
  const [sortBy, setSortBy] = useState('name-asc');
  const [expandedGPs, setExpandedGPs] = useState<Record<string, boolean>>({});
  const [approvedCards, setApprovedCards] = useState<Set<string>>(new Set());
  const [dismissedCards, setDismissedCards] = useState<Set<string>>(new Set());
  const toggleGP = (gp: string) => {
    setExpandedGPs(prev => ({
      ...prev,
      [gp]: !prev[gp]
    }));
  };
  const handleApprove = (id: string) => {
    setApprovedCards(prev => new Set(prev).add(id));
  };
  const handleDismiss = (id: string) => {
    setDismissedCards(prev => new Set(prev).add(id));
  };
  // Apply initial filter when it changes
  useEffect(() => {
    if (initialFilter) {
      setStatusFilter('');
      setGpFilter('');
      setUrgencyFilter('');
      setStaleOnly(false);
      if (initialFilter.key === 'urgency') setUrgencyFilter(initialFilter.value);
      if (initialFilter.key === 'status') setStatusFilter(initialFilter.value);
      if (initialFilter.key === 'stale') {
        setStaleOnly(true);
        setSortBy('last-stale');
      }
    }
  }, [initialFilter]);
  const rawData = normalizeData(projectName);
  const uniqueGPs = Array.from(new Set(rawData.map(d => d.gp))).sort();
  const filters: FilterDef[] = [{
    key: 'gp',
    label: 'Filter by GP',
    options: uniqueGPs.map(gp => ({
      value: gp,
      label: gp
    })),
    width: 'w-56'
  }, {
    key: 'status',
    label: 'Filter by Status',
    options: [{
      value: 'Fully Executed',
      label: 'Fully Executed'
    }, {
      value: 'Pending Execution',
      label: 'Pending Execution'
    }, {
      value: 'Not Yet Circulated',
      label: 'Not Yet Circulated'
    }],
    width: 'w-56'
  }];
  if (projectName !== 'Diamond') {
    filters.push({
      key: 'urgency',
      label: 'Filter by Urgency',
      options: [{
        value: 'high',
        label: 'Urgent'
      }, {
        value: 'medium',
        label: 'Moderate'
      }, {
        value: 'low',
        label: 'Low'
      }],
      width: 'w-48'
    });
  }
  const sortOptions: SortOption[] = [{
    value: 'name-asc',
    label: 'Partnership A–Z'
  }, {
    value: 'name-desc',
    label: 'Partnership Z–A'
  }, {
    value: 'urgency-high',
    label: 'Urgency (High first)'
  }, {
    value: 'last-stale',
    label: 'Last Contact (Stale)'
  }];
  const filterValues = {
    gp: gpFilter,
    status: statusFilter,
    urgency: urgencyFilter
  };
  const handleFilterChange = (key: string, value: string) => {
    if (key === 'gp') setGpFilter(value);
    if (key === 'status') setStatusFilter(value);
    if (key === 'urgency') setUrgencyFilter(value);
  };
  const handleClearAll = () => {
    setGpFilter('');
    setStatusFilter('');
    setUrgencyFilter('');
    setStaleOnly(false);
  };
  const filteredData = rawData.filter(item => {
    if (dismissedCards.has(item.id)) return false;
    if (gpFilter && item.gp !== gpFilter) return false;
    if (statusFilter && item.statusCategory !== statusFilter) return false;
    if (urgencyFilter && item.urgency !== urgencyFilter) return false;
    if (staleOnly && item.rawElapsedDays <= 14) return false;
    return true;
  });
  const urgencyOrder: Record<string, number> = {
    high: 0,
    medium: 1,
    low: 2
  };
  const sortedData = [...filteredData].sort((a, b) => {
    if (sortBy === 'name-asc') return a.partnership.localeCompare(b.partnership);
    if (sortBy === 'name-desc') return b.partnership.localeCompare(a.partnership);
    if (sortBy === 'urgency-high') {
      const urgA = a.urgency ? urgencyOrder[a.urgency] : 3;
      const urgB = b.urgency ? urgencyOrder[b.urgency] : 3;
      return urgA - urgB;
    }
    if (sortBy === 'last-stale') {
      return b.rawElapsedDays - a.rawElapsedDays;
    }
    return 0;
  });
  const inProgressCount = sortedData.filter(d => d.stage !== 'Not Started').length;
  const circulatedCount = sortedData.filter(d => d.stage === 'Pending Execution' || d.stage === 'Complete').length;
  const kycStartedCount = sortedData.filter(d => d.stage === 'DDQ / Diligence' || d.stage === 'TA Drafting' || d.stage === 'Pending Execution' || d.stage === 'Complete').length;
  // Group data by GP for the Accordion view
  const groupedData = sortedData.reduce((acc, item) => {
    if (!acc[item.gp]) acc[item.gp] = [];
    acc[item.gp].push(item);
    return acc;
  }, {} as Record<string, UnifiedConsent[]>);
  const sortedGPs = Object.keys(groupedData).sort();
  const urgencyBorderClass = (urgency?: Urgency): string => {
    if (urgency === 'high') return 'border-l-2 border-l-rose-400';
    if (urgency === 'medium') return 'border-l-2 border-l-amber-300';
    return '';
  };
  return <div className="animate-in fade-in duration-300" data-id="element-6">
      <div className="flex items-start justify-between mb-6" data-id="element-7">
        <FilterBar filters={filters} filterValues={filterValues} onFilterChange={handleFilterChange} onClearAll={handleClearAll} sortOptions={sortOptions} sortValue={sortBy} onSortChange={setSortBy} actions={<button className="flex items-center border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 rounded-md px-3 py-1.5 text-sm font-medium transition-colors" data-id="element-9">
              <ExternalLink className="w-4 h-4 mr-2" data-id="element-10" />
              Export
            </button>} data-id="element-8" />

        {/* View Toggle */}
        <div className="flex bg-gray-100 p-1 rounded-lg ml-4 mt-4" data-id="element-11">
          <button onClick={() => setView('pipeline')} className={`flex items-center px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${view === 'pipeline' ? 'bg-white text-[#458b85] shadow-sm' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-12">
            <Columns3 className="w-4 h-4 mr-2" data-id="element-13" />
            Pipeline
          </button>
          <button onClick={() => setView('gp')} className={`flex items-center px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${view === 'gp' ? 'bg-white text-[#458b85] shadow-sm' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-14">
            <List className="w-4 h-4 mr-2" data-id="element-15" />
            GP View
          </button>
        </div>
      </div>

      {/* Summary Bar */}
      <div className="bg-white border border-gray-200 rounded-lg p-4 mb-6 shadow-sm flex items-center text-sm text-gray-600" data-id="element-16">
        <span className="font-medium text-gray-900 mr-2" data-id="element-17">
          {sortedData.length} partnerships
        </span>
        <span className="mx-2 text-gray-300" data-id="element-18">•</span>
        <span className="font-medium text-[#458b85] mr-2" data-id="element-19">
          {inProgressCount} in progress
        </span>
        <span className="mx-2 text-gray-300" data-id="element-20">•</span>
        <span data-id="element-21">{circulatedCount} TAs circulated</span>
        <span className="mx-2 text-gray-300" data-id="element-22">•</span>
        <span data-id="element-23">{kycStartedCount} KYC started</span>
      </div>

      {view === 'pipeline' ? <div className="flex gap-6 overflow-x-auto pb-4 min-h-[600px]" data-id="element-24">
          {STAGES.map(stage => {
        const stageCards = sortedData.filter(c => c.stage === stage);
        return <div key={stage} className="flex-1 min-w-[320px] bg-[#f4f5f5] rounded-xl p-4 flex flex-col border border-gray-200/60" data-id="element-25">
                <div className="flex items-center justify-between mb-4 px-1" data-id="element-26">
                  <h3 className="font-bold text-gray-800 text-sm" data-id="element-27">{stage}</h3>
                  <span className="bg-gray-200 text-gray-600 text-xs font-medium px-2 py-0.5 rounded-full" data-id="element-28">
                    {stageCards.length}
                  </span>
                </div>

                <div className="flex flex-col gap-3 flex-1" data-id="element-29">
                  {stageCards.length > 0 ? stageCards.map((card, index) => {
              const isPendingAI = card.aiBanner && !approvedCards.has(card.id);
              const tier = getElapsedTier(card.rawElapsedDays);
              const tierColor = TIER_TEXT_COLOR[tier];
              return <motion.div layout initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                scale: 0.95
              }} transition={{
                delay: index * 0.05,
                duration: 0.2
              }} key={card.id} className={`rounded-lg p-4 shadow-sm border ${isPendingAI ? 'border-dashed border-violet-300 bg-violet-50/40' : card.stage !== 'Not Started' ? 'bg-white border-amber-200 border-l-4 border-l-amber-400' : 'bg-white border-gray-200'} ${urgencyBorderClass(card.urgency)}`} data-id="element-30">
                          <div className={`transition-opacity duration-300 ${isPendingAI ? 'opacity-60' : 'opacity-100'}`} data-id="element-31">
                            <div className="flex justify-between items-start mb-1" data-id="element-32">
                              <div className="flex items-start gap-2 flex-wrap" data-id="element-33">
                                <h4 className="font-semibold text-gray-900 text-sm leading-tight" data-id="element-34">
                                  {card.partnership}
                                </h4>
                                {isPendingAI && <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-violet-100 text-violet-700 border border-violet-200 mt-0.5" data-id="element-35">
                                    <Sparkles className="w-3 h-3 mr-1" data-id="element-36" />
                                    AI Detected
                                  </span>}
                              </div>
                              {!isPendingAI && card.stage !== 'Not Started' && <AlertCircle className="w-4 h-4 text-amber-500 flex-shrink-0 ml-2" data-id="element-37" />}
                            </div>
                            <div className="mb-2" data-id="element-38">
                              <GpBadge name={card.gp} onClick={e => {
                      e.stopPropagation();
                      onGpSelect?.(card.gp);
                    }} data-id="element-39" />
                            </div>
                            {card.taStatus && <div className="mt-3 pt-3 border-t border-gray-100" data-id="element-40">
                                <p className="text-xs text-gray-500 mb-1 font-medium" data-id="element-41">
                                  TA Status:
                                </p>
                                <p className="text-xs text-gray-700" data-id="element-42">
                                  <SourceAnnotation sourceType="email" sourceTitle="Re: TA Status Update" sourceDate={card.lastCommDate || 'Recent'} extractedValue={card.taStatus} context="Transfer Agreement Status" data-id="element-43">
                                    {card.taStatus}
                                  </SourceAnnotation>
                                </p>
                              </div>}
                            {card.nextAction && <div className="mt-3 pt-3 border-t border-gray-100" data-id="element-44">
                                <p className="text-xs text-gray-600 flex items-start gap-1.5" data-id="element-45">
                                  <span className="w-1.5 h-1.5 rounded-full bg-amber-400 mt-1 flex-shrink-0" data-id="element-46" />
                                  <SourceAnnotation sourceType="document" sourceTitle="Action Items Tracker.pdf" sourceDate={card.lastCommDate || 'Recent'} extractedValue={card.nextAction} context="Next Required Action" data-id="element-47">
                                    {card.nextAction}
                                  </SourceAnnotation>
                                </p>
                              </div>}
                            <div className="mt-3 pt-3 border-t border-gray-100 flex justify-between items-center" data-id="element-48">
                              <div className="flex flex-col" data-id="element-49">
                                <span className={`text-[10px] font-mono font-semibold ${tierColor}`} data-id="element-50">
                                  {card.lastComm || '—'}
                                </span>
                                <span className="text-[10px] text-gray-400" data-id="element-51">
                                  <SourceAnnotation sourceType="email" sourceTitle="Latest Correspondence" sourceDate={card.lastCommDate || 'Recent'} extractedValue={card.lastCommDate || '—'} context="Last Communication Date" data-id="element-52">
                                    {card.lastCommDate || '—'}
                                  </SourceAnnotation>
                                </span>
                              </div>
                              <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${getInvoiceColor(card.invoice)}`} data-id="element-53">
                                {card.invoice || '—'}
                              </span>
                            </div>
                          </div>

                          {isPendingAI && <div className="mt-4 pt-3 border-t border-violet-200/60" data-id="element-54">
                              <p className="text-xs text-gray-600 mb-3 font-medium" data-id="element-55">
                                Is this partnership name correct?
                              </p>
                              <div className="flex justify-end gap-3" data-id="element-56">
                                <button onClick={() => handleDismiss(card.id)} className="text-xs font-medium text-gray-500 hover:text-gray-700 transition-colors" data-id="element-57">
                                  No, remove
                                </button>
                                <button onClick={() => handleApprove(card.id)} className="text-xs font-medium bg-[#458b85] hover:bg-[#3a7570] text-white px-3 py-1.5 rounded transition-colors shadow-sm" data-id="element-58">
                                  Yes, correct
                                </button>
                              </div>
                            </div>}
                        </motion.div>;
            }) : <div className="flex-1 flex items-center justify-center border-2 border-dashed border-gray-200 rounded-lg" data-id="element-59">
                      <p className="text-sm text-gray-400 font-medium" data-id="element-60">
                        No partnerships at this stage
                      </p>
                    </div>}
                </div>
              </div>;
      })}
        </div> : <div className="bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm" data-id="element-61">
          <div className="overflow-x-auto" data-id="element-62">
            <table className="w-full text-left border-collapse" data-id="element-63">
              <thead data-id="element-64">
                <tr className="bg-gray-50 border-b border-gray-200" data-id="element-65">
                  <th className="py-3 px-5 text-xs font-semibold text-gray-500 uppercase tracking-wider min-w-[300px]" data-id="element-66">
                    Fund Manager / Partnership
                  </th>
                  <th className="py-3 px-5 text-xs font-semibold text-gray-500 uppercase tracking-wider w-1/4" data-id="element-67">
                    Transfer Agreement Status
                  </th>
                  <th className="py-3 px-5 text-xs font-semibold text-gray-500 uppercase tracking-wider w-1/3" data-id="element-68">
                    Next Action
                  </th>
                  <th className="py-3 px-5 text-xs font-semibold text-gray-500 uppercase tracking-wider text-right whitespace-nowrap" data-id="element-69">
                    Last Comm
                  </th>
                  <th className="py-3 px-5 text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-70">
                    Invoice
                  </th>
                </tr>
              </thead>
              <tbody data-id="element-71">
                {sortedGPs.map(gp => {
              const gpCards = groupedData[gp];
              const isExpanded = expandedGPs[gp];
              const hasActivity = gpCards.some(c => c.stage !== 'Not Started');
              const hasPendingAI = gpCards.some(c => c.aiBanner && !approvedCards.has(c.id));
              return <Fragment key={gp} data-id="element-72">
                      {/* GP Header Row */}
                      <tr className={`cursor-pointer hover:bg-gray-50/50 transition-colors ${hasActivity ? 'border-l-2 border-l-amber-400' : ''} ${!isExpanded ? 'border-b border-gray-100' : ''}`} onClick={() => toggleGP(gp)} data-id="element-73">
                        <td className="py-4 px-5 align-middle" data-id="element-74">
                          <div className="flex items-center gap-2" data-id="element-75">
                            <div className="text-gray-400" data-id="element-76">
                              {isExpanded ? <ChevronDown className="w-4 h-4" data-id="element-77" /> : <ChevronRight className="w-4 h-4" data-id="element-78" />}
                            </div>
                            <GpBadge name={gp} onClick={e => {
                        e.stopPropagation();
                        onGpSelect?.(gp);
                      }} data-id="element-79" />
                            <span className="inline-flex items-center bg-gray-100 text-gray-500 text-xs font-medium px-2 py-0.5 rounded ml-2" data-id="element-80">
                              {gpCards.length}{' '}
                              {gpCards.length === 1 ? 'partnership' : 'partnerships'}
                            </span>
                            {hasPendingAI && <span className="inline-flex items-center bg-violet-100 text-violet-700 text-xs font-medium px-2 py-0.5 rounded ml-2 border border-violet-200" data-id="element-81">
                                Needs Review
                              </span>}
                          </div>
                        </td>
                        <td className="py-4 px-5 align-middle" colSpan={2} data-id="element-82">
                          {hasActivity ? <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-amber-50 text-amber-700 border border-amber-100" data-id="element-83">
                              <span className="w-1.5 h-1.5 rounded-full bg-amber-500 mr-1.5" data-id="element-84"></span>
                              In Progress
                            </span> : <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600 border border-gray-200" data-id="element-85">
                              Not Started
                            </span>}
                        </td>
                        <td className="py-4 px-5 align-middle text-right whitespace-nowrap" data-id="element-86">
                          {gpCards[0]?.lastComm && <>
                              <span className="text-sm font-mono font-semibold text-gray-500" data-id="element-87">
                                {gpCards[0].lastComm}
                              </span>
                              <div className="text-xs mt-0.5 text-gray-400" data-id="element-88">
                                {gpCards[0].lastCommDate}
                              </div>
                            </>}
                        </td>
                        <td className="py-4 px-5 align-middle" data-id="element-89"></td>
                      </tr>

                      {/* Expanded Partnership Rows */}
                      <AnimatePresence data-id="element-90">
                        {isExpanded && <>
                            <tr data-id="element-91">
                              <td colSpan={5} className="p-0" data-id="element-92">
                                <div className="h-px bg-gray-200 mx-0" data-id="element-93" />
                              </td>
                            </tr>
                            {gpCards.map((card, idx) => {
                      const isLast = idx === gpCards.length - 1;
                      const isPendingAI = card.aiBanner && !approvedCards.has(card.id);
                      const tier = getElapsedTier(card.rawElapsedDays);
                      const tierColor = TIER_TEXT_COLOR[tier];
                      return <motion.tr layout initial={{
                        opacity: 0,
                        backgroundColor: '#ffffff'
                      }} animate={{
                        opacity: 1,
                        backgroundColor: isPendingAI ? '#f5f3ff' : '#f9fafb'
                      }} exit={{
                        opacity: 0
                      }} key={card.id} className={`
                                    ${hasActivity && !isPendingAI ? 'border-l-2 border-l-amber-400' : ''} 
                                    ${urgencyBorderClass(card.urgency)}
                                    ${isPendingAI ? 'border-y border-dashed border-violet-300' : isLast ? 'border-b border-gray-200' : 'border-b border-gray-100'}
                                  `} data-id="element-94">
                                  <td className="py-3.5 px-5 pl-12 align-top" data-id="element-95">
                                    <div className={`flex flex-col items-start gap-2 transition-opacity duration-300 ${isPendingAI ? 'opacity-60' : 'opacity-100'}`} data-id="element-96">
                                      <div className="flex items-center gap-2 flex-wrap" data-id="element-97">
                                        <span className="text-sm text-gray-800 font-medium" data-id="element-98">
                                          {card.partnership}
                                        </span>
                                        {isPendingAI && <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-violet-100 text-violet-700 border border-violet-200" data-id="element-99">
                                            <Sparkles className="w-3 h-3 mr-1" data-id="element-100" />
                                            Needs Review
                                          </span>}
                                      </div>
                                    </div>
                                    {isPendingAI && <div className="mt-3" data-id="element-101">
                                        <p className="text-xs text-gray-600 mb-2 font-medium" data-id="element-102">
                                          Is this partnership name correct?
                                        </p>
                                        <div className="flex items-center gap-3" data-id="element-103">
                                          <button onClick={e => {
                                e.stopPropagation();
                                handleApprove(card.id);
                              }} className="text-xs font-medium bg-[#458b85] hover:bg-[#3a7570] text-white px-3 py-1.5 rounded transition-colors shadow-sm" data-id="element-104">
                                            Yes, correct
                                          </button>
                                          <button onClick={e => {
                                e.stopPropagation();
                                handleDismiss(card.id);
                              }} className="text-xs font-medium text-gray-500 hover:text-gray-700 transition-colors" data-id="element-105">
                                            No, remove
                                          </button>
                                        </div>
                                      </div>}
                                  </td>
                                  <td className={`py-3.5 px-5 align-top transition-opacity duration-300 ${isPendingAI ? 'opacity-60' : 'opacity-100'}`} data-id="element-106">
                                    <div className="space-y-1" data-id="element-107">
                                      {card.taStatus ? card.taStatus.split(';').map((point, i) => <div key={i} className="flex items-start gap-2 text-xs text-gray-500 leading-relaxed" data-id="element-108">
                                              <span className="text-gray-300 flex-shrink-0" data-id="element-109">
                                                —
                                              </span>
                                              <SourceAnnotation sourceType="email" sourceTitle="Re: TA Status Update" sourceDate={card.lastCommDate || 'Recent'} extractedValue={point.trim()} context="Transfer Agreement Status" data-id="element-110">
                                                <span data-id="element-111">{point.trim()}</span>
                                              </SourceAnnotation>
                                            </div>) : <span className="text-gray-300" data-id="element-112">—</span>}
                                    </div>
                                  </td>
                                  <td className={`py-3.5 px-5 align-top transition-opacity duration-300 ${isPendingAI ? 'opacity-60' : 'opacity-100'}`} data-id="element-113">
                                    <span className="text-sm text-gray-600 leading-relaxed" data-id="element-114">
                                      {card.nextAction ? <SourceAnnotation sourceType="document" sourceTitle="Action Items Tracker.pdf" sourceDate={card.lastCommDate || 'Recent'} extractedValue={card.nextAction} context="Next Required Action" data-id="element-115">
                                          {card.nextAction}
                                        </SourceAnnotation> : <span className="text-gray-300" data-id="element-116">—</span>}
                                    </span>
                                  </td>
                                  <td className={`py-3.5 px-5 align-top text-right whitespace-nowrap transition-opacity duration-300 ${isPendingAI ? 'opacity-60' : 'opacity-100'}`} data-id="element-117">
                                    {card.lastComm ? <>
                                        <span className={`text-sm font-mono font-semibold ${tierColor}`} data-id="element-118">
                                          {card.lastComm}
                                        </span>
                                        <div className="text-xs mt-0.5 text-gray-400" data-id="element-119">
                                          <SourceAnnotation sourceType="email" sourceTitle="Latest Correspondence" sourceDate={card.lastCommDate || 'Recent'} extractedValue={card.lastCommDate || '—'} context="Last Communication Date" data-id="element-120">
                                            {card.lastCommDate}
                                          </SourceAnnotation>
                                        </div>
                                      </> : <span className="text-gray-300" data-id="element-121">—</span>}
                                  </td>
                                  <td className={`py-3.5 px-5 align-top transition-opacity duration-300 ${isPendingAI ? 'opacity-60' : 'opacity-100'}`} data-id="element-122">
                                    {card.invoice ? <div className="flex flex-col gap-1" data-id="element-123">
                                        <span className={`inline-flex items-center text-xs font-medium px-2.5 py-1 rounded-full whitespace-nowrap w-fit ${getInvoiceColor(card.invoice)}`} data-id="element-124">
                                          {card.invoice}
                                        </span>
                                        {card.invoiceAmount && <div className="text-xs mt-1" data-id="element-125">
                                            <SourceAnnotation sourceType="document" sourceTitle="Invoice Schedule.pdf" sourceDate={card.lastCommDate || 'Recent'} extractedValue={card.invoiceAmount} context="Invoice Amount" data-id="element-126">
                                              <span className="text-gray-800 font-medium" data-id="element-127">
                                                {card.invoiceAmount}
                                              </span>
                                            </SourceAnnotation>
                                            <span className="text-gray-400 ml-1" data-id="element-128">
                                              {card.invoiceSplit} (B/S)
                                            </span>
                                          </div>}
                                      </div> : <span className="text-gray-300" data-id="element-129">—</span>}
                                  </td>
                                </motion.tr>;
                    })}
                          </>}
                      </AnimatePresence>
                    </Fragment>;
            })}
              </tbody>
            </table>
          </div>
        </div>}
    </div>;
}